import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { type MouseEvent, useEffect, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import {
  type SalePTBFFixation,
  useCancelSalePTBFFixationMutation,
  useGetSalePTBFFixationsQuery,
} from '../../store/api/salePTBFFixationsApi'
import SalePTBFFixationFormDialog from './SalePTBFFixationFormDialog'
import SalePTBFFixationPrintDialog from './SalePTBFFixationPrintDialog'
import { usePagePermission } from '../../hooks/usePagePermission'

type ColumnKind = 'text' | 'date' | 'number'
type ColumnDef = { key: keyof SalePTBFFixation; label: string; kind?: ColumnKind; align?: 'right' }

const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none',
  fontSize: '0.7rem',
  lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': {
      borderColor: alpha(theme.palette.text.primary, 0.24),
      backgroundColor: alpha(theme.palette.text.primary, 0.04),
    },
  },
})

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contractNo', label: 'Contract No' },
  { key: 'contractSale', label: 'Contract Sale' },
  { key: 'buyersRef', label: "Buyer's Ref" },
  { key: 'cpCode', label: 'Client Ref' },
  { key: 'shortName', label: 'Client Short' },
  { key: 'name', label: 'Client' },
  { key: 'quality', label: 'Grade' },
  { key: 'description', label: 'Description' },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'mtons', label: 'Mtons', kind: 'number', align: 'right' },
  { key: 'noOfLots', label: 'No of Lots', kind: 'number', align: 'right' },
  { key: 'balanceLotsTbf', label: 'Balance Lots TBF', kind: 'number', align: 'right' },
  { key: 'lotsFixed', label: 'Lots Fixed', kind: 'number', align: 'right' },
  { key: 'levelFixed', label: 'Level Fixed', kind: 'number', align: 'right' },
  { key: 'ptbfDiffs', label: 'PTBF Diffs', kind: 'number', align: 'right' },
  { key: 'finalPrice', label: 'Final Price', kind: 'number', align: 'right' },
  { key: 'dateFix', label: 'Date Fixed', kind: 'date' },
  { key: 'releaseDate', label: 'Release Date', kind: 'date' },
  { key: 'futuresMonth', label: 'Futures Month' },
  { key: 'fxRoe', label: 'FX ROE', kind: 'number', align: 'right' },
  { key: 'uploadText', label: 'Upload' },
  { key: 'customerSignedText', label: 'Customer Signed' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'date' },
  { key: 'personUpdate', label: 'Updated Person' },
]

const NON_SUM_TOTAL_KEYS = new Set<keyof SalePTBFFixation>(['finalPrice', 'fxRoe', 'levelFixed', 'ptbfDiffs'])
const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`.slice(-2)
  return `${day}-${month}-${year}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SalePTBFFixation[], key: keyof SalePTBFFixation): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

const rowMatchesSearch = (row: SalePTBFFixation, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return TABLE_COLUMNS.some((col) => {
    const value = row[col.key]
    if (value == null) return false
    return String(value).toLowerCase().includes(needle)
  })
}

export default function SalePTBFFixationListView() {
  const { canEdit } = usePagePermission()
  const [selectedContractNo, setSelectedContractNo] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterPopover, setFilterPopover] = useState<{ anchor: HTMLElement } | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'new' | 'edit'>('new')
  const [printLang, setPrintLang] = useState<'en' | 'vn' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [cancelFixation, { isLoading: isDeleting }] = useCancelSalePTBFFixationMutation()

  const { data, isLoading, isFetching, error } = useGetSalePTBFFixationsQuery({
    page: 1,
    pageSize: ALL_ROWS_PAGE_SIZE,
  })

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(
    () =>
      baseRows.filter((row) => {
        if (!rowMatchesSearch(row, searchTerm)) return false
        return true
      }),
    [baseRows, searchTerm],
  )
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return filteredRows
    const start = page * rowsPerPage
    return filteredRows.slice(start, start + rowsPerPage)
  }, [filteredRows, page, rowsPerPage])
  const selectedRow = useMemo(
    () => filteredRows.find((row) => row.contractNo === selectedContractNo) ?? null,
    [filteredRows, selectedContractNo],
  )
  const totalRowValues = useMemo(
    () =>
      new Map(
        TABLE_COLUMNS.filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(col.key)).map((col) => [
          col.key,
          sumNumericColumn(filteredRows, col.key),
        ]),
      ),
    [filteredRows],
  )
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`

  const closeFilterPopover = () => setFilterPopover(null)

  const hasActiveFilters = Boolean(searchTerm.trim())

  useEffect(() => {
    setPage(0)
  }, [searchTerm])

  const clearAllFilters = () => {
    setSearchTerm('')
    setPage(0)
    closeFilterPopover()
  }

  const openForm = (mode: 'new' | 'edit') => {
    if (mode === 'edit' && !selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    setFormMode(mode)
    setFormOpen(true)
  }

  const handleDelete = async () => {
    if (!selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    const ok = window.confirm(`Do you want to delete this PTBF fixation = '${selectedRow.contractNo}'?`)
    if (!ok) return
    try {
      await cancelFixation(selectedRow.id ?? 0).unwrap()
      setSelectedContractNo(null)
      window.alert('Successfully Deleted')
    } catch {
      setActionError('Could not delete PTBF fixation.')
    }
  }

  const cellValue = (row: SalePTBFFixation, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  const idRangeText = useMemo(() => {
    const ids = rows.map((r) => Number(r.id)).filter(Number.isFinite)
    if (!ids.length) return 'ID: —'
    return `ID: ${Math.min(...ids)}-${Math.max(...ids)}`
  }, [rows])

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Filter
          </Typography>
          <Button
            size="small"
            variant={searchTerm.trim() ? 'contained' : 'outlined'}
            color={searchTerm.trim() ? 'primary' : 'inherit'}
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e: MouseEvent<HTMLElement>) => setFilterPopover({ anchor: e.currentTarget })}
            sx={filterBarBtnSx}
          >
            Search
          </Button>
          {hasActiveFilters && (
            <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>
              Clear All
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => openForm('new')}>
            New
          </Button>
          <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => openForm('edit')}>
            Edit
          </Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || isDeleting || !canEdit} onClick={handleDelete}>
            Delete
          </Button>
          <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={() => setPrintLang('en')}>
            Pr - Fixation E
          </Button>
          <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={() => setPrintLang('vn')}>
            Pr - Fixation V
          </Button>
        </Stack>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load PTBF fixation data.
        </Alert>
      ) : null}
      {actionError ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      <Box className="app-dataframe">
          <Box
            className="app-dataframe-toolbar"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              flexWrap: 'wrap',
              minHeight: 30,
              py: 0.125,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
              {(isLoading || isFetching) && <CircularProgress size={15} />}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
              >
                Total: {filteredRows.length}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
              >
                Loaded: {baseRows.length}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem' }}
              >
                {idRangeText}
              </Typography>
              <TablePagination
                component="div"
                count={filteredRows.length}
                page={rowsPerPage === -1 ? 0 : page}
                onPageChange={(_, value) => setPage(value)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(Number(e.target.value))
                  setPage(0)
                }}
                rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
                labelRowsPerPage="Rows"
                sx={{
                  flexShrink: 0,
                  '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.72rem' },
                  '& .MuiTablePagination-select': { fontSize: '0.72rem' },
                  '& .MuiTablePagination-actions': { transform: 'scale(0.92)', transformOrigin: 'center' },
                }}
              />
            </Stack>
          </Box>

          <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {TABLE_COLUMNS.map((col) => (
                    <TableCell key={col.key} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {TABLE_COLUMNS.map((col, index) => {
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: SalePTBFFixation) =>
                TABLE_COLUMNS.map((col) => {
                  const val = cellValue(row, col)
                  return (
                    <TableCell key={col.key} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 3600, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const row = Number.isFinite(idx) ? rows[idx] : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={row != null && row.contractNo === selectedContractNo}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (row) setSelectedContractNo(row.contractNo)
                      }}
                    />
                  )
                },
              }}
            />
          </Box>
      </Box>

      <Popover
        open={Boolean(filterPopover)}
        anchorEl={filterPopover?.anchor ?? null}
        onClose={closeFilterPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1, width: 360 }}>
          <Stack spacing={1}>
            <TextField
              label="Search"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button size="small" onClick={clearAllFilters}>
                Clear
              </Button>
              <Button size="small" variant="contained" onClick={closeFilterPopover}>
                Apply
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>

      <SalePTBFFixationFormDialog
        open={formOpen}
        mode={formMode}
        contractNo={selectedRow?.contractNo}
        onClose={() => setFormOpen(false)}
      />
      <SalePTBFFixationPrintDialog
        open={printLang != null}
        language={printLang ?? 'en'}
        fixation={selectedRow}
        onClose={() => setPrintLang(null)}
        canEdit={canEdit}
      />
    </Box>
  )
}
